import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Copy, UserPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useProjectCtx } from "@/context/ProjectContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { PROJECT_ROLES, PROJECT_STATUSES } from "@/lib/roles";
import { getErrorMessage } from "@/lib/errors";
import { confirmAction, showNotice } from "@/lib/dialogs";
import { linkedRecordsPath } from "@/lib/linkedRecords";
import { CompanyField } from "@/components/CompanyField";

export default function Settings() {
  const { can } = usePermissions();
  const canCloneProject = can("PROJECT_LIST", "create");
  const canDeleteProject = can("PROJECT_LIST", "delete");
  const { projectId, project, isProjectManager, refresh } = useProjectCtx();
  const { companies } = useCompany() || {};
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => { if (project) setForm(project); }, [project]);
  useEffect(() => {
    api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const loadMembers = useCallback(async () => {
    try {
      const [{ data: m }, { data: u }] = await Promise.all([
        api.get(`/projects/${projectId}/members`),
        api.get("/users"),
      ]);
      setMembers(m);
      setOrgUsers(u);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const saveProject = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { name, spv_name, location, dc_capacity_mwp, ac_capacity_mw, status, start_date, target_end_date, actual_end_date, doc_no, notes, company_id, client_id, contract_value } = form;
      await api.patch(`/projects/${projectId}`, {
        name, spv_name, location, status, notes, doc_no,
        company_id: company_id || null,
        client_id: client_id || null,
        contract_value: contract_value ? Number(contract_value) : null,
        dc_capacity_mwp: dc_capacity_mwp ? Number(dc_capacity_mwp) : null,
        ac_capacity_mw: ac_capacity_mw ? Number(ac_capacity_mw) : null,
        start_date: start_date || null,
        target_end_date: target_end_date || null,
        actual_end_date: actual_end_date || null,
      });
      toast.success("Project settings saved");
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (userId, role) => {
    try {
      await api.post(`/projects/${projectId}/members`, { user_id: userId, role });
      toast.success("Member added");
      loadMembers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const removeMember = async (userId) => {
    try {
      await api.delete(`/projects/${projectId}/members/${userId}`);
      toast.success("Member removed");
      loadMembers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // A project can only be deleted before anything is entered against it. If entries exist we explain which, and offer no Delete.
  const showBlocked = (blockers) => showNotice({
    kind: "warning", title: "Project cannot be deleted",
    description: "This project has existing records linked to it. Remove or reassign the linked records before trying again.",
    itemsHeading: "Linked records", items: blockers.map((b) => ({ key: b.table, label: b.name, count: b.count, href: linkedRecordsPath(b.table, projectId) })),
    footnote: "The project's own set-up (activities, estimate, budget and team) is removed with it once nothing else is linked.",
  });

  const deleteProject = async () => {
    try {
      const { data: check } = await api.get(`/projects/${projectId}/delete-check`);
      if (!check.can_delete) return showBlocked(check.blockers);
      const ok = await confirmAction({
        kind: "danger", title: "Delete this project?", confirmLabel: "Delete project",
        message: `Are you sure you want to delete ${project.name}? Its activities, estimate, budget and team are removed. This action cannot be undone.`,
      });
      if (!ok) return;
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      navigate("/");
    } catch (err) {
      if (err?.response?.status === 409) {   // something was entered after we checked; show it the same way
        try { const { data: check } = await api.get(`/projects/${projectId}/delete-check`); if (!check.can_delete) return showBlocked(check.blockers); } catch { /* fall through to the message */ }
      }
      toast.error(getErrorMessage(err));
    }
  };

  if (!form) return null;
  const availableUsers = orgUsers.filter((u) => !members.some((m) => m.user_id === u.id));

  return (
    <div className="space-y-6" data-testid="project-settings-page">
      <PageHeader
        title="Project Settings"
        description={isProjectManager ? "You can edit this project's fields and manage its team." : "Read-only — only a Project Manager can edit."}
        action={
          <div className="flex gap-2">
            {canCloneProject && <CloneDialog projectId={projectId} project={project} />}
            {canDeleteProject && (
              <Button variant="destructive" size="sm" onClick={deleteProject} data-testid="delete-project-button"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            )}
          </div>
        }
      />

      <form onSubmit={saveProject} className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-2" data-testid="project-settings-form">
        <fieldset disabled={!isProjectManager} className="contents">
          <div>
            <Label>Name</Label>
            <Input data-testid="settings-name-input" value={form.name} onChange={set("name")} className="mt-1.5" />
          </div>
          <div>
            <Label>Location</Label>
            <Input data-testid="settings-location-input" value={form.location || ""} onChange={set("location")} className="mt-1.5" />
          </div>
          <CompanyField label="Company" value={form.company_id || ""} onChange={(v) => setForm({ ...form, company_id: v })} testId="settings-company-select" required={false} placeholder="Unassigned" />
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="settings-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Client</Label>
            <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="settings-client-select" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contract value (₹)</Label>
            <Input type="number" step="0.01" data-testid="settings-contract-value-input" value={form.contract_value ?? ""} onChange={set("contract_value")} className="mt-1.5" />
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" data-testid="settings-start-date-input" value={form.start_date || ""} onChange={set("start_date")} className="mt-1.5" />
          </div>
          <div>
            <Label>Target end date</Label>
            <Input type="date" data-testid="settings-target-end-date-input" value={form.target_end_date || ""} onChange={set("target_end_date")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Textarea data-testid="settings-notes-input" value={form.notes || ""} onChange={set("notes")} className="mt-1.5" />
          </div>
        </fieldset>
        {isProjectManager && (
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} data-testid="settings-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
            </Button>
          </div>
        )}
      </form>

      <div className="rounded-xl border border-border bg-card" data-testid="project-members-section">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Team members</h2>
          {isProjectManager && <AddMemberDialog users={availableUsers} onAdd={addMember} />}
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Designation</TableHead><TableHead>Project role</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.user_id}`}>
                <TableCell className="text-foreground">{m.user?.full_name}</TableCell>
                <TableCell className="text-muted-foreground">{m.user?.email}</TableCell>
                <TableCell className="text-muted-foreground">{m.user?.designation || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{m.role}</TableCell>
                <TableCell className="text-right">
                  {isProjectManager && (
                    <Button size="sm" variant="ghost" onClick={() => removeMember(m.user_id)} data-testid={`remove-member-btn-${m.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AddMemberDialog({ users, onAdd }) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("VIEWER");
  const [designations, setDesignations] = useState([]);
  useResetOnClose(Boolean(open), () => { setUserId(""); setRole("VIEWER"); });      // closed without saving: opens fresh next time
  useEffect(() => {
    if (open) api.get("/masters/designations").then(({ data }) => setDesignations(data)).catch(() => {});
  }, [open]);
  // Choosing a person pre-selects the project role their designation maps to (still changeable).
  const pickUser = (id) => {
    setUserId(id);
    const u = users.find((x) => x.id === id);
    const d = designations.find((x) => x.id === u?.designation_id);
    if (d) setRole(d.project_role);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-member-button"><UserPlus className="h-3.5 w-3.5" /> Add member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a team member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Organisation user</Label>
            <Select value={userId} onValueChange={pickUser}>
              <SelectTrigger data-testid="add-member-user-select" className="mt-1.5"><SelectValue placeholder="Select a user" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.designation ? ` — ${u.designation}` : ""} ({u.email})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Project role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="add-member-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!userId} data-testid="add-member-submit-button" onClick={() => { onAdd(userId, role); setOpen(false); setUserId(""); }}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloneDialog({ projectId, project }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: `${project.name} (Copy)`, start_date: "", target_end_date: "", shift_schedule: true });
  const navigate = useNavigate();

  const submit = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/clone`, { ...form, start_date: form.start_date || null, target_end_date: form.target_end_date || null });
      toast.success(`Cloned into ${data.code}`);
      setOpen(false);
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="clone-project-button"><Copy className="h-3.5 w-3.5" /> Clone</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Clone project structure</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>New code</Label><Input data-testid="clone-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" /></div>
          <div><Label>New name</Label><Input data-testid="clone-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>New start date</Label><Input type="date" data-testid="clone-start-date-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button disabled={!form.code} onClick={submit} data-testid="clone-submit-button">Clone</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}